import { Observable } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';

import { GetRoundsOptions } from '@models/get-rounds-options.interface';
import { PagedResponse } from '@models/paged-response.model';
import { PredictorRoundQuestionPredictions } from '@models/predictor-round-question-predictions.model';
import { RoundListItem } from '@models/round-list-item.model';
import { RoundsSortField } from '@models/rounds-sort-field.enum';
import { RoundStatusModel } from '@models/round-status.model';

import { environment } from '@env/environment';

export abstract class BaseRoundService<
  RoundDetail = unknown,
  PredictorQuestion = unknown,
  UserPrediction = unknown,
  RoundPredictionStatusModel = unknown,
> {
  public http = inject(HttpClient);
  public readonly _apiUrl = environment.apiUrl;
  private readonly _subUrl: string;

  protected constructor(subUrl: string) {
    this._subUrl = subUrl;
  }

  get roundUrl() {
    return `${this._apiUrl}/${this._subUrl}`;
  }

  getRoundById(uuid: string): Observable<RoundDetail> {
    return this.http.get<RoundDetail>(`${this.roundUrl}/${uuid}`);
  }

  getRoundQuestions(
    roundUuid: string,
    pageNumber = 1,
    pageSize = 10,
    search = '',
  ): Observable<PagedResponse<PredictorQuestion>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());

    if (search.trim()) {
      params = params.set('search', search);
    }

    return this.http.get<PagedResponse<PredictorQuestion>>(
      `${this.roundUrl}/${roundUuid}/questions`,
      { params },
    );
  }

  savePredictions(
    roundUuid: string,
    predictions: PredictorRoundQuestionPredictions[],
  ): Observable<UserPrediction[]> {
    return this.http.post<UserPrediction[]>(`${this.roundUrl}/${roundUuid}/predictions`, {
      predictions,
    });
  }

  getPredictionsByRound(
    roundUuid: string,
    pageNumber = 1,
    pageSize = 10,
  ): Observable<PagedResponse<UserPrediction>> {
    const params = { pageNumber, pageSize };
    return this.http.get<PagedResponse<UserPrediction>>(
      `${this.roundUrl}/${roundUuid}/predictions`,
      { params },
    );
  }

  getPredictionStatus(roundUuid: string): Observable<RoundPredictionStatusModel> {
    return this.http.get<RoundPredictionStatusModel>(
      `${this.roundUrl}/${roundUuid}/predictions/status`,
    );
  }

  getRounds(options: GetRoundsOptions = {}): Observable<PagedResponse<RoundListItem>> {
    const {
      pageNumber = 1,
      pageSize = 100,
      search,
      types,
      gameTypeUuids,
      statusUuids,
      roundsUuids,
      sortedBy = RoundsSortField.CREATED_AT_DESC,
    } = options;

    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('sortedBy', sortedBy);

    if (search?.trim()) {
      params = params.set('search', search.trim());
    }

    if (types?.length) {
      params = params.set('types', types.join(','));
    }

    if (gameTypeUuids?.length) {
      params = params.set('gameTypeUuids', gameTypeUuids.join(','));
    }

    if (statusUuids?.length) {
      params = params.set('statusUuids', statusUuids.join(','));
    }

    if (roundsUuids?.length) {
      params = params.set('roundsUuids', roundsUuids.join(','));
    }

    return this.http.get<PagedResponse<RoundListItem>>(`${this._apiUrl}/rounds`, { params });
  }

  getRoundStatuses(pageNumber = 1, pageSize = 100): Observable<PagedResponse<RoundStatusModel>> {
    return this.http.get<PagedResponse<RoundStatusModel>>(`${this._apiUrl}/round-statuses`, {
      params: { pageNumber, pageSize },
    });
  }
}
